import { useNavigate } from 'react-router-dom';
import { XCircle, ArrowLeft, CreditCard, HelpCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

export default function BillingCancelPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-orange-50">
      <div className="container mx-auto px-4 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="max-w-2xl mx-auto text-center"
        >
          {/* Cancel Icon */}
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.3, type: "spring", stiffness: 200 }}
            className="mx-auto w-20 h-20 bg-orange-100 rounded-full flex items-center justify-center mb-6"
          >
            <XCircle className="w-10 h-10 text-orange-600" />
          </motion.div>

          {/* Cancel Message */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
          >
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Payment Cancelled
            </h1>
            <p className="text-xl text-gray-600 mb-8">
              No worries! Your payment was cancelled and no charges were made to your account.
            </p>
          </motion.div>

          {/* Options Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
            className="mb-8"
          >
            <Card className="border-2 border-orange-200 bg-white/80 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="text-2xl font-bold text-gray-900">
                  What would you like to do?
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-4 md:grid-cols-2">
                  {/* Try Again */}
                  <div className="p-4 border border-gray-200 rounded-lg">
                    <div className="flex items-center space-x-3 mb-3">
                      <CreditCard className="w-6 h-6 text-indigo-600" />
                      <h3 className="text-lg font-semibold">Try Again</h3>
                    </div>
                    <p className="text-gray-600 mb-4 text-sm">
                      Ready to upgrade? We'd love to have you on our Starter or Pro plan.
                    </p>
                    <Button
                      onClick={() => navigate('/dashboard')}
                      className="w-full bg-indigo-600 hover:bg-indigo-700"
                    >
                      View Pricing Plans
                    </Button>
                  </div>

                  {/* Continue Free */}
                  <div className="p-4 border border-gray-200 rounded-lg">
                    <div className="flex items-center space-x-3 mb-3">
                      <ArrowLeft className="w-6 h-6 text-purple-600" />
                      <h3 className="text-lg font-semibold">Continue Free</h3>
                    </div>
                    <p className="text-gray-600 mb-4 text-sm">
                      Keep using Memara with your free plan. You can upgrade anytime.
                    </p>
                    <Button
                      onClick={() => navigate('/spaces')}
                      variant="outline"
                      className="w-full border-purple-200 text-purple-600 hover:bg-purple-50"
                    >
                      Enter Memory Workspace
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Help Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9 }}
            className="space-y-4"
          >
            <Card className="bg-gray-50 border-gray-200">
              <CardContent className="p-6">
                <div className="flex items-center justify-center space-x-3 mb-4">
                  <HelpCircle className="w-6 h-6 text-gray-600" />
                  <h3 className="text-lg font-semibold text-gray-900">Need Help?</h3>
                </div>
                <p className="text-gray-600 mb-4">
                  If you experienced any issues during checkout or have questions about our plans, we're here to help.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <Button
                    variant="outline"
                    className="border-gray-300 text-gray-700 hover:bg-gray-100"
                    onClick={() => window.open('mailto:support@memara.io?subject=Billing Support Needed')}
                  >
                    Contact Support
                  </Button>
                  <Button
                    variant="outline"
                    className="border-gray-300 text-gray-700 hover:bg-gray-100"
                    onClick={() => navigate('/dashboard')}
                  >
                    View Dashboard
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Reassurance */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.1 }}
            className="mt-8 p-4 bg-green-50 rounded-lg border border-green-200"
          >
            <p className="text-sm text-green-800">
              <strong>Secure & Safe:</strong> Your payment information is always protected. 
              No charges were made to your account.
            </p>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
} 